import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { ReactNode } from "react";
import { X } from "lucide-react";

interface AdminSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  /** sm | md | lg | xl | full */
  size?: "sm" | "md" | "lg" | "xl" | "full";
  side?: "right" | "left" | "bottom";
}

const widthMap: Record<NonNullable<AdminSheetProps["size"]>, string> = {
  sm: "sm:max-w-md",
  md: "sm:max-w-2xl",
  lg: "sm:max-w-4xl",
  xl: "sm:max-w-6xl",
  full: "sm:max-w-[min(96vw,1400px)]",
};

/**
 * Unified slide-over wrapper for all Admin Portal drill-ins.
 * Every admin pop-up MUST use this so UX stays consistent:
 * - dark glass surface, gold accent header, blurred backdrop
 * - scrollable body, sticky header
 */
export function AdminSheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  size = "lg",
  side = "right",
}: AdminSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={side}
        className={`${widthMap[size]} w-full p-0 bg-background border-l border-amber-500/30 text-foreground flex flex-col overflow-y-auto [&>button]:hidden`}
      >
        <SheetHeader className="px-6 py-4 pr-14 border-b border-amber-500/20 bg-card sticky top-0 z-10">
          <SheetTitle className="text-foreground font-display text-xl">
            {title}
          </SheetTitle>
          {description && (
            <SheetDescription className="text-muted-foreground">
              {description}
            </SheetDescription>
          )}
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="absolute right-4 top-4 z-20 h-8 w-8 inline-flex items-center justify-center rounded-md border border-border/50 bg-background/80 text-muted-foreground hover:text-foreground hover:bg-background transition"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-6 py-6">{children}</div>
      </SheetContent>
    </Sheet>
  );
}
